import type { Section } from './api';
import type { Tier } from './format';

const optionName = (s: Section, id: string) => s.options.find((o) => o.id === id)?.name ?? id;

/** The one sentence at the top of a multivariate test's results. */
export function sectionsHeadline(
  tier: Tier,
  recommended: Record<string, string>,
  sections: Section[],
): string {
  switch (tier) {
    case 'confirmed': {
      const better = sections
        .filter((s) => recommended[s.id] && recommended[s.id] !== s.options[0]!.id)
        .map((s) => `${s.name}: ${optionName(s, recommended[s.id]!)}`);
      return better.length
        ? `Better options found: ${better.join('; ')}.`
        : 'The original is better in every section that has a clear answer.';
    }
    case 'leaning':
      return 'Some options look better, but nothing is proven yet.';
    case 'too_early':
      return 'Too early to tell.';
    case 'no_meaningful_difference':
      return 'No difference worth acting on.';
    case 'check_data':
      return 'Something is wrong with the visitor numbers.';
  }
}

/** What to do about the winning options, in words. */
export function shipAdvice(recommended: Record<string, string>, sections: Section[]): string {
  const parts = sections
    .filter((s) => recommended[s.id])
    .map((s) =>
      recommended[s.id] === s.options[0]!.id
        ? `${s.name}: keep the original`
        : `${s.name}: use ${optionName(s, recommended[s.id]!)}`,
    );
  return parts.length
    ? `To act on this: ${parts.join('; ')}. Sections without a clear answer yet are not included.`
    : '';
}

/** One line about one section's verdict. */
export function sectionLine(tier: Tier, winnerId: string | null, section: Section): string {
  const winner = winnerId ? optionName(section, winnerId) : '';
  const original = winnerId === section.options[0]!.id;
  switch (tier) {
    case 'confirmed':
      return original ? 'The original is best.' : `${winner} is the winner.`;
    case 'leaning':
      return original
        ? 'The original looks best, but it is not proven yet.'
        : `${winner} looks better, but it is not proven yet.`;
    case 'too_early':
      return 'Too early to tell.';
    case 'no_meaningful_difference':
      return 'No difference worth acting on.';
    case 'check_data':
      return 'The visitor numbers look wrong.';
  }
}
